import type {ComponentProps, ReactNode} from 'react';
import React from 'react';

import CSSModules from 'react-css-modules';

import {getHeadingColourFromTextColour} from '@edume/bento/slides';

import Link from '../../Link';
import ShrinkText from '../../ShrinkText';
import Text from '../../TextComponents/Text';
import CompletionButton from './CompletionButton';
import Tick from './Tick';

import styles from './styles.module.scss';

type ConditionalProps1 =
  | {
      buttonUrl: string;
      goToDashboardText?: never;
    }
  | {
      buttonUrl?: never;
      goToDashboardText: string;
    };

type ConditionalProps2 =
  | {
      showDashboardLink?: true;
      goToDashboardText: string;
    }
  | {
      showDashboardLink?: false;
      goToDashboardText?: never;
    };

type CommonProps = {
  goToDashboard: () => void;
  numberOfActivities: number;
  numberOfActivitiesText: string;
  title: string;
  subtitle?: string;
  message?: string;
  buttonText?: string;
  scrollEnded?: boolean;
  reskinClassName?: string;
  background?: string;
  showDashboardLink?: boolean;
  textColour?: '#FFFFFF' | '#1E1E34';
  isButtonLightTheme?: boolean;
  imageUrl?: string;
  ticksRendered?: boolean[];
  isPreviewMode?: boolean;
  isCoursePreview?: boolean;
  getCoursePreviewButton?: () => ReactNode;
  isLoading?: boolean;
  isInAdminPanel?: boolean;
  tickBoxAltText: string;
  getCompletionTicksAriaLabel: ComponentProps<
    typeof Tick
  >['getCompletionTicksAriaLabel'];
};

type CompletionScreenProps = CommonProps &
  ConditionalProps1 &
  ConditionalProps2;

// eslint-disable-next-line complexity
const CompletionScreen = (
  /* prettier-ignore */ {
    numberOfActivities, numberOfActivitiesText, title, subtitle, message,
    buttonUrl, buttonText, scrollEnded = true, reskinClassName = '',
    goToDashboard, goToDashboardText, background, showDashboardLink, textColour, isButtonLightTheme,
    imageUrl, ticksRendered = [], isPreviewMode = false,
    isCoursePreview = false, getCoursePreviewButton = () => null,
    isLoading = false, isInAdminPanel = false, tickBoxAltText, getCompletionTicksAriaLabel
  }: CompletionScreenProps
): JSX.Element => {
  const goToDashboardIfNotACoursePreview = () =>
    isCoursePreview ? null : goToDashboard();

  // eslint-disable-next-line complexity
  const renderActions = () => {
    const containerStyle = `action-container ${scrollEnded ? 'visible' : ''}`;

    const missingHttp =
      buttonUrl && typeof buttonUrl === 'string' && !buttonUrl.includes('://');

    const renderCustomLink = (customLink: string) => {
      // Returns null if blocked (WebView/popup blocker)
      const newWindow = window.open(customLink, '_blank');
      if (!newWindow) {
        window.location.href = customLink;
      }
      return newWindow;
    };

    const buttonOnClick = () =>
      buttonUrl
        ? renderCustomLink(missingHttp ? `https://${buttonUrl}` : buttonUrl)
        : goToDashboardIfNotACoursePreview();

    return (
      <>
        <div styleName={containerStyle}>
          <ShrinkText
            maxPxWidth={isInAdminPanel ? 220 : 300}
            startingFontSize={isInAdminPanel ? 'm' : 'l'}
            minimumFontSize={isInAdminPanel ? 'xs' : 's'}
          >
            <CompletionButton
              onClick={buttonOnClick}
              text={buttonText}
              url={buttonUrl}
              isButtonLightTheme={isButtonLightTheme}
              isLoading={isLoading}
              isCoursePreview={isCoursePreview}
              isInAdminPanel={isInAdminPanel}
            />
          </ShrinkText>

          {showDashboardLink && (
            <Link
              onClick={goToDashboardIfNotACoursePreview}
              text={goToDashboardText}
              linkColour={getHeadingColourFromTextColour(textColour)}
              linkSize='snail'
              disabled={isCoursePreview}
              keepEnabledTextColour
            />
          )}
        </div>
        {isCoursePreview && getCoursePreviewButton()}
      </>
    );
  };

  const imageStyles = {
    width: '100%',
    background: `url(${imageUrl}) center/contain no-repeat`,
  };

  const previewContainerClass = isPreviewMode ? 'preview-mode' : '';

  return (
    <div
      data-automation='slide-container'
      styleName={`container ${reskinClassName} ${previewContainerClass}`}
      style={{backgroundColor: background || 'transparent'}}
    >
      <div
        data-automation='icon-image'
        styleName={`completion-image end ${scrollEnded ? 'visible' : ''}`}
        style={imageStyles}
      />
      <div styleName={`content ${scrollEnded ? 'visible' : ''}`}>
        <div styleName={previewContainerClass}>
          <h2
            style={{color: textColour}}
            styleName='heading'
            data-automation='title-text'
          >
            {title}
          </h2>
          <div styleName='tick-container'>
            <Tick
              numberOfTicks={numberOfActivities}
              ticksRendered={ticksRendered ? ticksRendered : []}
              isPreviewMode={isPreviewMode}
              tickBoxAltText={tickBoxAltText}
              getCompletionTicksAriaLabel={getCompletionTicksAriaLabel}
            />
            {!isPreviewMode && (
              <Text
                size='m'
                colour={textColour === '#FFFFFF' ? 'grey050' : 'textPrimary'}
                dataAutomation='activity-count'
              >
                {numberOfActivitiesText}
              </Text>
            )}
          </div>
          {!reskinClassName && (
            <div styleName='subHeading-container'>
              <h2
                data-automation='subtitle-text'
                styleName='subHeading'
                style={{color: textColour}}
              >
                {subtitle}
              </h2>
            </div>
          )}
          <p
            styleName='body'
            style={{color: textColour}}
            data-automation='description-text'
          >
            {message}
          </p>
        </div>
        {!isPreviewMode && renderActions()}
      </div>
    </div>
  );
};

export default CSSModules(CompletionScreen, styles, {allowMultiple: true});
